/**
 * Résolution des deep-links — Phase 7, incrément 7.2c.
 *
 * Quand l'app native (iOS/Android) est ouverte via un lien
 * (`appUrlOpen` de `@capacitor/app`), l'OS fournit l'URL complète qui
 * a déclenché l'ouverture. On la normalise en une route hash interne
 * pour que le router maison (ADR 0004) prenne le relais.
 *
 * Formes d'URL supportées (toutes mènent au même token) :
 *   - Universal/App Link : `https://nestor.app/invite/<token>`
 *   - Avec hash explicite : `https://nestor.app/#/invite/<token>`
 *   - Custom scheme       : `app.nestor.app://invite/<token>`
 *   - Chemin nu (fallback): `/invite/<token>`
 *
 * **Pur** : aucune dépendance DOM/Capacitor → testable isolément. Le
 * câblage natif (`lib/capacitor/app.ts`) et le routage (`main.ts`)
 * consomment le résultat.
 *
 * Sécurité : on n'extrait QUE le pattern `/invite/<token>` connu. Tout
 * autre lien retourne `null` (pas de navigation arbitraire pilotée par
 * un lien externe).
 */

/** Longueurs de token acceptées — alignées sur l'API (`tokenParamsSchema`). */
const TOKEN_MIN = 20;
const TOKEN_MAX = 64;

/**
 * Extrait un token d'invitation d'un segment de chemin du type
 * `/invite/<token>` (ou `invite/<token>`). Retourne `null` si le
 * pattern n'est pas présent ou si le token est hors bornes.
 */
function extractInviteToken(path: string): string | null {
  // Normalise : enlève un éventuel `/` initial, coupe query/fragment.
  const clean = path.replace(/^\/+/, '').split('?')[0]!.split('#')[0]!;
  const match = /^invite\/([^/]+)$/.exec(clean);
  if (!match) return null;
  const token = decodeURIComponent(match[1]!);
  if (token.length < TOKEN_MIN || token.length > TOKEN_MAX) return null;
  return token;
}

/**
 * Résout une URL de deep-link en route hash interne
 * (`#/invite/<token>`), ou `null` si l'URL n'est pas un lien
 * d'invitation reconnu.
 *
 * Le résultat est destiné à être posé sur `window.location.hash` :
 * le router (`hashchange`) normalise alors `/invite/<token>` → route
 * `/invite` et `parseInviteToken()` récupère le token.
 */
export function resolveDeepLink(rawUrl: string): string | null {
  let token: string | null = null;

  try {
    const u = new URL(rawUrl);
    // 1) Forme hash : `…/#/invite/<token>`.
    if (u.hash) {
      token = extractInviteToken(u.hash.replace(/^#/, ''));
    }
    // 2) Custom scheme `scheme://invite/<token>` → host = 'invite',
    //    pathname = '/<token>'.
    if (!token && u.host === 'invite') {
      token = extractInviteToken(`invite/${u.pathname.replace(/^\/+/, '')}`);
    }
    // 3) Universal link `https://domain/invite/<token>`.
    if (!token) {
      token = extractInviteToken(u.pathname);
    }
  } catch {
    // Pas une URL absolue → tente comme chemin nu (`/invite/<token>`).
    token = extractInviteToken(rawUrl);
  }

  return token ? `#/invite/${token}` : null;
}
